import { useAgentVisibility } from '@/entities/agent';
import {
  getCurrentOrganizationId,
  useIsWorkspacePermissionsResolving,
  usePermissions,
} from '@/entities/user';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { CONTENT_STUDIO_TOOL_ID } from './managedTools';
import { useContentStudioEnabled } from './useContentStudioEnabled';

export interface ContentStudioAllowed {
  isAllowed: boolean;
  isLoading: boolean;
}

/**
 * The single gate for Content Studio access: every surface reads this hook so the
 * rollout flags, the per-workspace ACL and the user's role cannot drift into disagreeing
 * conditions. `isLoading` is surfaced separately from `isAllowed` so a consumer can hold a
 * tile back, or a route can hold its redirect, while the ACL check or the workspace's
 * permissions are still in flight.
 */
export const useContentStudioAllowed = (): ContentStudioAllowed => {
  const workspaceId = useCurrentWorkspaceId();
  const organizationId = getCurrentOrganizationId();
  const { canViewContentStudio } = usePermissions();
  const isPermissionsResolving = useIsWorkspacePermissionsResolving(workspaceId);
  const isFlagEnabled = useContentStudioEnabled();
  const { isAgentEnabled, isLoading } = useAgentVisibility([CONTENT_STUDIO_TOOL_ID], {
    workspaceId,
    organizationId,
  });

  return {
    isAllowed: isFlagEnabled && isAgentEnabled(CONTENT_STUDIO_TOOL_ID) && canViewContentStudio,
    isLoading: isLoading || isPermissionsResolving,
  };
};
